import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Calendar } from "lucide-react";
import { Popover, TextField, MenuItem } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AirDatepicker from "air-datepicker";
import "air-datepicker/air-datepicker.css";
import "./air-datepicker-custom.css";
import "./custom-datepicker.scss";
import localeEn from "air-datepicker/locale/en";
import { customRanges, DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS, TEXT_LIBRARY } from "../../utils/constants";
import { getDateRangeFormat, getRange, getMaxTime } from "../../utils/utility";

const CustomDatePicker = forwardRef(
  ({ onChange, isRangePicker = true, showCustom = true, maxAllowedDays = 2 }, ref) => {
    useImperativeHandle(ref, () => ({
      handleCustomChange: (e, setRange) => handleCustomChange(e, setRange),
    }));
    const [anchorEl, setAnchorEl] = useState(null);
    const [range, setRange] = useState(getRange(DEFAULT_TIME_RANGE));
    const [custom, setCustom] = useState(DEFAULT_TIME_RANGE);
    const [selectedDate, setSelectedDate] = useState(getRange(DEFAULT_TIME_RANGE));
    const [datepickerDiv, setDatepickerDiv] = useState(null);
    const datepickerInstanceRef = useRef(null);
    const [startTime, setStartTime] = useState(dayjs().subtract(12, "hour"));
    const [endTime, setEndTime] = useState(dayjs());

    // Today's date for maxDate
    const today = dayjs().endOf("day").toDate();

    useEffect(() => {
      if (!anchorEl) {
        setDatepickerDiv(null);
        if (datepickerInstanceRef.current) {
          datepickerInstanceRef.current.destroy();
          datepickerInstanceRef.current = null;
        }
        return;
      }
      const div = document.createElement("div");
      setDatepickerDiv(div);
      let dp;
      setTimeout(() => {
        dp = new AirDatepicker(div, {
          range: isRangePicker,
          multipleDatesSeparator: " - ",
          locale: localeEn,
          maxDate: today, // Block future dates
          toggleSelected: ({ datepicker }) => {
            if (datepicker.selectedDates.length > 1) {
              return true;
            }
            return false;
          },
          onSelect: ({ date, datepicker }) => {
            if (Array.isArray(date) && date.length === 2) {
              // when both start and end dates are selected
              const sortedDates = date.map(d => dayjs(d)).sort((a, b) => a - b);
              // Block future dates in range selection
              const endDate = sortedDates[1].endOf("day");
              const todayDayjs = dayjs(today);
              const validEndDate = endDate.isAfter(todayDayjs) ? todayDayjs : endDate;
              const dateRange = [sortedDates[0].startOf("day"), validEndDate];
              setSelectedDate(dateRange);
              setCustom(RANGE_TYPE_LABELS.CUSTOM);
              datepicker.update(
                {
                  minDate: "",
                  maxDate: today,
                  selectedDates: dateRange,
                },
                { silent: true }
              );
            } else if (Array.isArray(date) && date.length === 1) {
              // when only start date is selected
              const selectedDay = dayjs(date[0]);
              const startOfDay = selectedDay.startOf("day");
              setSelectedDate([startOfDay]);
              const minAllowed = selectedDay.subtract(maxAllowedDays, "day").toDate();
              let potentialMaxDate = selectedDay.add(maxAllowedDays, "day");
              if (potentialMaxDate > today) {
                potentialMaxDate = today;
              }
              const maxAllowed = potentialMaxDate;
              datepicker.update(
                {
                  minDate: minAllowed,
                  maxDate: maxAllowed,
                  selectedDates: startOfDay,
                },
                { silent: true }
              );
              setCustom(RANGE_TYPE_LABELS.CUSTOM);
            } else if (Array.isArray(date) && date.length === 0) {
              // when dates are cleared
              datepicker.update(
                {
                  minDate: "",
                  maxDate: today,
                  selectedDates: [],
                },
                { silent: true }
              );
            }
          },
          autoClose: false,
          selectedDates: Array.isArray(range) && range.length > 0 ? range.map(d => (d ? d.toDate() : d)) : [],
        });
        datepickerInstanceRef.current = dp;
      }, 0);
      return () => {
        if (datepickerInstanceRef.current && typeof datepickerInstanceRef.current.destroy === "function") {
          datepickerInstanceRef.current.destroy();
        }
        datepickerInstanceRef.current = null;
      };
    }, [anchorEl, isRangePicker]);

    const handleCustomChange = (e, setRangeBool = false) => {
      const value = e.target.value;
      setCustom(value);
      const newRange = getRange(value);
      setSelectedDate(newRange);
      if (setRangeBool) {
        setRange(newRange);
      }
      if (datepickerInstanceRef.current && value !== RANGE_TYPE_LABELS.CUSTOM) {
        const dp = datepickerInstanceRef.current;
        dp.selectDate([newRange[0].toDate(), newRange[1].toDate()], { silent: true });
      }
    };

    const handleConfirm = () => {
      let selectedDateRange = selectedDate;
      if (selectedDateRange?.length === 1) {
        selectedDateRange = [selectedDate[0].startOf("day"), selectedDate[0].endOf("day")];
        setSelectedDate(selectedDateRange);
        if (datepickerInstanceRef.current) {
          datepickerInstanceRef.current.update(
            {
              selectedDates: selectedDateRange,
            },
            { silent: true }
          );
        }
      }

      if (custom === RANGE_TYPE_LABELS.CUSTOM && selectedDateRange?.length === 2) {
        let alignedStart = selectedDateRange[0].hour(startTime.hour()).minute(startTime.minute());
        let alignedEnd = selectedDateRange[1].hour(endTime.hour()).minute(endTime.minute());

        // If start date is today
        const now = dayjs();
        if (selectedDateRange[0].isSame(now, "day")) {
          const twelveHoursBefore = now.subtract(12, "hour");
          // If 12 hours before is still today, set to that
          if (twelveHoursBefore.isSame(now, "day")) {
            alignedStart = selectedDateRange[0].hour(twelveHoursBefore.hour()).minute(twelveHoursBefore.minute());
          } else {
            // If going back 12 hours would cross into yesterday, force to 12:00 AM
            alignedStart = selectedDateRange[0].hour(0).minute(0);
          }
        }

        setStartTime(alignedStart);
        setEndTime(alignedEnd);
        selectedDateRange = [alignedStart, alignedEnd];
      }
      setRange(selectedDateRange);
      if (onChange) onChange(selectedDateRange, custom);
      setAnchorEl(null);
    };

    const handleCancel = () => {
      setSelectedDate(range);
      setAnchorEl(null);
    };

    const popupContent = (
      <div className="p-6 min-w-[300px] flex flex-col gap-4">
        {datepickerDiv && <div ref={node => node && !node.hasChildNodes() && node.appendChild(datepickerDiv)} />}
        {/* NEW TIME PICKERS */}
        {custom === RANGE_TYPE_LABELS.CUSTOM && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="flex gap-4 mt-2">
              <TimePicker
                label={TEXT_LIBRARY.START_TIME}
                value={startTime}
                onChange={setStartTime}
                sx={{ width: 135 }}
                maxTime={getMaxTime(selectedDate[0])}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    className: "custom-timepicker",
                  },
                }}
              />

              <TimePicker
                label={TEXT_LIBRARY.END_TIME}
                value={endTime}
                onChange={setEndTime}
                sx={{ width: 135 }}
                maxTime={getMaxTime(selectedDate[1] || selectedDate[0])}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    className: "custom-timepicker",
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        )}
        {showCustom && (
          <TextField
            select
            value={custom}
            onChange={handleCustomChange}
            size="small"
            variant="outlined"
            className="mb-2 bg-white"
          >
            {customRanges
              .filter(opt => opt.noOfDays <= maxAllowedDays + 1)
              .map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.85rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
          </TextField>
        )}
        <div className="flex justify-between gap-4 mt-1">
          <button
            className="w-1/2 bg-transparent text-black border border-gray-400 rounded-lg py-2 text-sm font-medium cursor-pointer"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="w-1/2 bg-orange-500 text-white border-none rounded-lg py-2 text-sm font-medium cursor-pointer"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    );

    return (
      <>
        <div className="relative">
          <TextField
            className="w-full custom-datepicker-field"
            value={getDateRangeFormat(range)}
            style={{ width: "400px" }}
            size="small"
            onClick={e => setAnchorEl(e.currentTarget)}
            InputProps={{
              startAdornment: (
                <span className="text-gray-400 pointer-events-none w-9">
                  <Calendar size={18} />
                </span>
              ),
              readOnly: true,
            }}
          />
        </div>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCancel}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {popupContent}
        </Popover>
      </>
    );
  }
);

CustomDatePicker.displayName = "CustomDatePicker";
export default CustomDatePicker;

CustomDatePicker.propTypes = {
  onChange: PropTypes.func,
  isRangePicker: PropTypes.bool,
  showCustom: PropTypes.bool,
  maxAllowedDays: PropTypes.number,
};
